/*
 * PAGE GROUPS (2026-10-21; docs/PAGE_GROUPS_DESIGN.md, phase 2): the calls. Every call names the siteId, and answers { ok, ... } or { ok: false, reason, detail }: a refusal is something a person did (a pattern
 * that would match every page), shown in words, never thrown. A request that could not be made at all is the reason "failed".
 */
import { site } from "../services/site";
import storage from "../util/storage";

export interface PageGroupWire {
  id: string;
  /** what visits are recorded under and the page list shows: the pattern for a single-pattern group, the name otherwise; never changes */
  key: string;
  name: string;
  patterns: string[];
  pages: string[];
  createdAt?: string;
}

export interface Candidate {
  pattern: string;
  visits: number;
  distinctSeen: number;
  examples: string[];
  firstSeen?: string;
  lastSeen?: string;
}

export interface Preview {
  distinctUrls: number;
  visits: number;
  sharePercent: number;
  sampleVisits: number;
  sampleUrls: number;
  examples: Array<{ url: string; visits: number }>;
  takenByOthers: number;
  /** matching pages that are in a slot: a pattern leaves those alone (an exact listing includes them) */
  heldInSlots: number;
  heldExamples: string[];
  absorbedPages: number;
  broad: boolean;
  lookbackDays: number;
}

export type Refusal = { ok: false; reason: string; detail?: string };
export type Members = { name?: string; patterns: string[]; pages: string[] };

const siteIdNow = (): string => String(storage.get("siteId") || "");

const call = async (method: string, params: Record<string, unknown>): Promise<any> => {
  try {
    const response = await site({ id: 0, jsonprc: "2.0", method, params: { siteId: siteIdNow(), ...params } } as any);

    return (response as any)?.data?.result ?? null;
  } catch {
    return null;
  }
};

const refusal = (answer: any): Refusal => ({ ok: false, reason: answer && typeof answer.reason === "string" ? answer.reason : "failed", ...(answer && answer.detail ? { detail: String(answer.detail) } : {}) });

export const fetchGroups = async (): Promise<PageGroupWire[] | null> => {
  const answer = await call("getPageGroups", {});

  return answer && answer.ok && Array.isArray(answer.groups) ? answer.groups : null;
};

export const fetchCandidates = async (): Promise<Candidate[] | null> => {
  const answer = await call("getPageCandidates", {});

  return answer && answer.ok && Array.isArray(answer.candidates) ? answer.candidates : null;
};

export const requestPreview = async (members: Members, groupId?: string): Promise<({ ok: true } & Preview) | Refusal> => {
  const answer = await call("previewPageGroup", { patterns: members.patterns, pages: members.pages, ...(groupId ? { groupId } : {}) });

  return answer && answer.ok ? answer : refusal(answer);
};

export type SaveResult = { ok: true; group: PageGroupWire; absorbed: number; pages: any[] } | Refusal;

export const requestCreate = async (members: Members): Promise<SaveResult> => {
  const answer = await call("createPageGroup", { name: members.name, patterns: members.patterns, pages: members.pages });

  return answer && answer.ok ? answer : refusal(answer);
};

export const requestUpdate = async (groupId: string, members: Members): Promise<SaveResult> => {
  const answer = await call("updatePageGroup", { groupId, name: members.name, patterns: members.patterns, pages: members.pages });

  return answer && answer.ok ? answer : refusal(answer);
};

export const requestDelete = async (groupId: string): Promise<({ ok: true; key?: string; pages: any[] }) | Refusal> => {
  const answer = await call("deletePageGroup", { groupId });

  return answer && answer.ok ? answer : refusal(answer);
};

/** what is inside a group page over a range: its page views split by the URLs visitors really had. The rows plus `otherViews` add up to `total`, the page's own count. */
export interface GroupContents {
  rows: Array<{ url: string; views: number }>;
  otherViews: number;
  total: number;
  distinctUrls: number;
}

/** the most addresses asked for: all of them, up to the server's own cap (a longer group still ends in one summed line) */
export const ALL_ADDRESSES = 1000;

export const requestGroupContents = async (key: string, startDate?: string, endDate?: string): Promise<GroupContents | null> => {
  try {
    const answer = await call("getGroupBreakdown", { key, startDate, endDate, limit: ALL_ADDRESSES });

    return answer && Array.isArray(answer.rows) ? (answer as GroupContents) : null;
  } catch {
    return null;
  }
};
